import React from "react";
import { Link } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";

const APP_STORE = "https://apps.apple.com/app/plexys/id6478015586";

const features = [
  { title: "Plexys Score", desc: "A composite gait health metric that gives you and your surgeon a clear picture", icon: <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="#2D5A3D" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 20V10"/><path d="M12 20V4"/><path d="M6 20v-6"/></svg> },
  { title: "Clinical Questionnaires", desc: "ODI, NDI, and mJOA — the same tools your surgeon uses", icon: <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="#2D5A3D" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 11l3 3L22 4"/><path d="M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h11"/></svg> },
  { title: "25 Guided Exercises", desc: "Spine-specific exercises with step-by-step guidance", icon: <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="#2D5A3D" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="5" r="3"/><path d="M12 8v4"/><path d="M8 12l4 4 4-4"/><path d="M6 20l6-4 6 4"/></svg> },
  { title: "Mobility Tracking", desc: "Connects with Apple Health to monitor your movement", icon: <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="#2D5A3D" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 12h-4l-3 9L9 3l-3 9H2"/></svg> },
  { title: "Find a Surgeon", desc: "Locate a spine surgeon near you", icon: <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="#2D5A3D" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/></svg> },
  { title: "Clinical PDF Export", desc: "Share your complete profile with your doctor", icon: <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="#2D5A3D" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg> },
  { title: "Pain & Symptom Journal", desc: "Track your symptoms over time to see patterns", icon: <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="#2D5A3D" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 20h9"/><path d="M16.5 3.5a2.121 2.121 0 013 3L7 19l-4 1 1-4L16.5 3.5z"/></svg> },
  { title: "Treatment History", desc: "Keep a complete record of your spine care journey", icon: <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="#2D5A3D" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="3" width="20" height="14" rx="2" ry="2"/><line x1="8" y1="21" x2="16" y2="21"/><line x1="12" y1="17" x2="12" y2="21"/></svg> },
];

const steps = [
  { num: "1", title: "Complete Your Profile", desc: "Answer a few questions about your spine health and symptoms" },
  { num: "2", title: "Track Daily", desc: "Log your symptoms, pain levels, and mobility each day" },
  { num: "3", title: "Share with Your Surgeon", desc: "Export your Plexys Score and clinical data before appointments" },
];

export default function PlexysAppLanding() {
  return (
    <div className="min-h-screen" style={{ backgroundColor: "#FAF8F5", color: "#1a1a1a" }}>
      {/* Nav */}
      <nav className="max-w-7xl mx-auto px-4 py-6 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3">
          <img src="/PlexysLogo012526.png" alt="Plexys" className="h-9 w-auto" />
          <span className="text-xl font-bold text-gray-900">Plexys</span>
        </Link>
        <a href={APP_STORE} className="text-sm font-medium px-5 py-2 rounded-full text-white" style={{ backgroundColor: "#2D5A3D" }}>
          Download
        </a>
      </nav>

      {/* Hero */}
      <section className="max-w-7xl mx-auto px-4 py-16 md:py-24">
        <div className="flex flex-col md:flex-row items-center gap-12">
          {/* Left side - text and CTA */}
          <div className="flex-1 text-left">
            <img src="/PlexysLogo012526.png" alt="Plexys" className="w-20 h-20 rounded-2xl mb-6" />
            <h1 className="text-4xl md:text-6xl font-bold text-gray-900 mb-4">Plexys Spine Health</h1>
            <p className="text-xl md:text-2xl text-gray-600 mb-8">Your complete spine health companion</p>
            <div className="flex flex-col sm:flex-row items-start gap-4 mb-8">
              <a href={APP_STORE} className="inline-flex items-center gap-2 px-6 py-3 rounded-xl text-white font-medium" style={{ backgroundColor: "#2D5A3D" }}>
                <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor"><path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/></svg>
                Download on the App Store
              </a>
            </div>
            <div className="inline-block p-4 rounded-2xl" style={{ backgroundColor: "rgba(45,90,61,0.08)" }}>
              <QRCodeSVG value={APP_STORE} size={120} bgColor="transparent" fgColor="#2D5A3D" />
              <p className="text-xs mt-2 text-gray-400">Scan to download</p>
            </div>
          </div>
          {/* Right side - doctor image */}
          <div className="flex-1">
            <img 
              src="/doctor-spine-consult.png" 
              alt="Doctor reviewing spine images with patient" 
              className="w-full rounded-3xl shadow-xl"
              style={{ maxHeight: "500px", objectFit: "cover" }}
            />
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="max-w-7xl mx-auto px-4 py-16">
        <h2 className="text-3xl md:text-4xl font-bold text-center text-gray-900 mb-12">Everything for Your Spine Health</h2>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {features.map((f, i) => (
            <div key={i} className="p-6 rounded-2xl bg-white shadow-sm border border-gray-100">
              <div className="w-10 h-10 rounded-lg flex items-center justify-center mb-3" style={{ backgroundColor: "rgba(45, 90, 61, 0.1)" }}>{f.icon}</div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">{f.title}</h3>
              <p className="text-sm text-gray-600">{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* How It Works */}
      <section className="py-16" style={{ backgroundColor: "#F0EDE8" }}>
        <div className="max-w-7xl mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold text-center text-gray-900 mb-12">How It Works</h2>
          <div className="grid md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            {steps.map((s) => (
              <div key={s.num} className="text-center">
                <div className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-4 text-xl font-bold text-white" style={{ backgroundColor: "#2D5A3D" }}>
                  {s.num}
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">{s.title}</h3>
                <p className="text-sm text-gray-600">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Credibility */}
      <section className="max-w-3xl mx-auto px-4 py-16 text-center">
        <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6" style={{ backgroundColor: "rgba(45, 90, 61, 0.1)" }}>
          <svg className="w-8 h-8" viewBox="0 0 24 24" fill="none" stroke="#2D5A3D" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/><path d="M9 12h6"/><path d="M12 9v6"/></svg>
        </div>
        <h2 className="text-3xl font-bold text-gray-900 mb-4">Built for Patients, Designed by a Surgeon</h2>
        <p className="text-lg text-gray-600 leading-relaxed">
          Plexys was created by a spine surgeon who saw patients struggling to track their symptoms and communicate
          effectively with their care team. Every feature is designed to bridge that gap — giving you the tools
          to take an active role in your spine health journey.
        </p>
      </section>

      {/* Pricing */}
      <section className="py-16 text-center" style={{ backgroundColor: "#F0EDE8" }}>
        <div className="max-w-3xl mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Completely Free</h2>
          <p className="text-lg text-gray-600 mb-8">No subscriptions. No in-app purchases. Just a better way to manage your spine health.</p>
          <a href={APP_STORE} className="inline-block px-8 py-4 rounded-xl text-white font-semibold text-lg" style={{ backgroundColor: "#2D5A3D" }}>
            Download Plexys Free
          </a>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-10 border-t border-gray-200">
        <div className="max-w-7xl mx-auto px-4 flex flex-col md:flex-row items-center justify-between gap-4 text-sm text-gray-400">
          <p>&copy; {new Date().getFullYear()} Plexys. All rights reserved.</p>
          <div className="flex gap-6">
            <Link to="/privacy" className="hover:text-gray-600 transition">Privacy Policy</Link>
            <Link to="/" className="hover:text-gray-600 transition">Plexys Home</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
